import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ConfigurationService, UrlService } from '@draeger-academy/common';
import { TranslateService } from '@ngx-translate/core';
import { ScrollToConfigOptions, ScrollToService } from '@andrei4ik/ngx-scroll-to';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { FormsService, HubspotFormData } from '../form-builder';
import { HubspotTrackingService } from './shared/services/hubspot-tracking.service';
import { HubspotService } from './shared/services/hubspot.service';
import { PrepopulationHandlingService } from './shared/services/prepopulation-handling.service';

@Component({
    selector: 'da-hubspot',
    templateUrl: './hubspot.component.html',
    styleUrls: ['./hubspot.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HubspotComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    @Input() hubspotId = '';
    @Input() webinarId = '';
    @Input() customFieldValues = null;
    public form: UntypedFormGroup;
    private _$reactiveFormData: BehaviorSubject<UntypedFormGroup> = new BehaviorSubject(null);
    private _$inlineMessage: BehaviorSubject<string> = new BehaviorSubject(null);
    private _$hubspotData: BehaviorSubject<HubspotFormData> = new BehaviorSubject(null);
    private _$currentHash: BehaviorSubject<string> = new BehaviorSubject(null);
    private _$legalConsents: BehaviorSubject<any> = new BehaviorSubject(null);
    private _$errorResponse: BehaviorSubject<string> = new BehaviorSubject(null);
    private _$submitted: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$hasMissingRequiredFields: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$submitLabel: BehaviorSubject<string> = new BehaviorSubject(null);
    private _hasBeenSeen: boolean;
    private _sub: Subscription;

    constructor(
        private _hubspotService: HubspotService,
        private _hubspotTracking: HubspotTrackingService,
        private _scrollToService: ScrollToService,
        private _formsService: FormsService,
        private _configuration: ConfigurationService,
        private _prepopulationHandlingService: PrepopulationHandlingService,
        protected _ref: ChangeDetectorRef,
        private _urlService: UrlService,
        private _translateService: TranslateService
    ) {}

    public get currentHash$(): Observable<string> {
        return this._$currentHash.asObservable();
    }
    public get hasMissingRequiredFields$(): Observable<boolean> {
        return this._$hasMissingRequiredFields.asObservable();
    }

    public get reactiveFormData$(): Observable<UntypedFormGroup> {
        return this._$reactiveFormData.asObservable();
    }

    public get errorResponse$(): Observable<string> {
        return this._$errorResponse.asObservable();
    }

    public get inlineMessage$(): Observable<string> {
        return this._$inlineMessage.asObservable();
    }

    public get submitted$(): Observable<boolean> {
        return this._$submitted.asObservable();
    }

    public get hubspotData$(): Observable<HubspotFormData> {
        return this._$hubspotData.asObservable();
    }

    public get legalConsents$(): Observable<any> {
        return this._$legalConsents.asObservable();
    }

    public get submitLabel$(): Observable<string> {
        return this._$submitLabel.asObservable();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hubspotId && changes.hubspotId.currentValue && changes.hubspotId.currentValue !== changes.hubspotId.previousValue) {
            if (changes.hubspotId.previousValue) {
                this._$hubspotData.next(null);
                this._$reactiveFormData.next(null);
                this._hubspotService.removeFormByGuid(changes.hubspotId.previousValue);
                this._loadHubspotData();
            }
        }
    }

    ngOnInit() {
        if (!this.hubspotId || this.hubspotId === '') {
            throw new Error('No Hubspot ID found');
        }

        this._loadHubspotData();
    }

    public ngOnDestroy(): void {
        this._hubspotService.removeFormByGuid(this.hubspotId);
    }

    public ngAfterViewInit(): void {
        this._hubspotTracking.loadTrackingScript(this.hubspotId);
    }

    public getRichTextWithoutTags(richText: any): string {
        if (richText.content) {
            return richText.content.replace(/<[^>]*>?/gm, '');
        }
        return null;
    }

    public onSubmit(e: Event) {
        e.preventDefault();
        this._$reactiveFormData.getValue().updateValueAndValidity();
        this._formsService.setSubmitting(true, this._$currentHash.getValue());
        if (!this._$reactiveFormData.getValue().valid) {
            this._setMainErrorIfNeeded();
            this.form.valueChanges.subscribe((val) => {
                this._setMainErrorIfNeeded();
            });
            this.scrollTo(this._$currentHash.getValue());
        }
        if (this._$reactiveFormData.getValue().valid) {
            this._submit();
        }
    }

    public onInViewportChange(event: any) {
        if (!this._hasBeenSeen && event === true) {
            this._hubspotTracking.trackInView(this.hubspotId);
            this._hasBeenSeen = true;
        }
    }

    public scrollTo($element) {
        const config: ScrollToConfigOptions = {
            target: $element,
            duration: 300,
            easing: 'easeInOutQuint',
            offset: -100
        };
        this._scrollToService.scrollTo(config);
    }

    private _setFormData(setPrepopData: boolean) {
        const _params = this._getUrlParams();
        this.form = this._hubspotService.getReactiveFormFromSchema(
            this.hubspotId,
            this._$currentHash.getValue(),
            true,
            {
                ..._params,
                ...this.customFieldValues
            },
            setPrepopData
        );
        this._$reactiveFormData.next(this.form);

        this.form.valueChanges.subscribe((val) => {
            if (!this.form.pristine) {
                this._hubspotTracking.trackFirstFormInteraction(this.hubspotId);
            }
        });
        this._hubspotTracking.trackFormReady(this.hubspotId);
    }

    private _submit() {
        this._hubspotService
            .postDataToHubspot(this.hubspotId, this._$reactiveFormData.getValue(), this._$currentHash.getValue(), this.webinarId)
            .subscribe((data) => {
                if (data.error && data.error.status && data.error.status === 'error') {
                    this._$errorResponse.next(data.error.message);
                    this._$submitted.next(true);
                } else {
                    this._hubspotTracking.trackSubmit(this.hubspotId);
                    if (data.redirectUri) {
                        window.location.href = data.redirectUri;
                    } else {
                        this._$inlineMessage.next(data.inlineMessage);
                        this._$submitted.next(true);
                    }
                }
            });
    }

    private _setMainErrorIfNeeded() {
        const fieldsWithError = [];
        Object.keys(this._$reactiveFormData.getValue().controls).forEach((key) => {
            if (this._$reactiveFormData.getValue().controls[key].hasError('required')) {
                fieldsWithError.push(this._$reactiveFormData.getValue().controls[key]);
            }
        });
        this._$hasMissingRequiredFields.next(fieldsWithError.length > 0);
    }

    private _getUrlParams() {
        let match;
        const pl = /\+/g, // Regex for replacing addition symbol with a space
            search = /([^&=]+)=?([^&]*)/g,
            decode = (s) => {
                return decodeURIComponent(s.replace(pl, ' '));
            },
            query = window.location.search.substring(1);

        const urlParams = {};
        while ((match = search.exec(query))) {
            urlParams[decode(match[1])] = decode(match[2]);
        }
        return urlParams;
    }

    private _setSubmitLabel(prom: any) {
        if (prom.submitText && prom.submitText !== '') {
            this._$submitLabel.next(prom.submitText);
        } else {
            this._translateService.get('hubspot'.toLowerCase()).subscribe((data) => this._$submitLabel.next(data.submitText));
        }
    }

    private _loadHubspotData() {
        this._sub = this._configuration.isSiteLoaded$.subscribe((isLoaded) => {
            if (isLoaded === true) {
                const _hash = this._urlService.encode(this.hubspotId + Math.random());
                let prom1: Promise<any>;
                prom1 = this._prepopulationHandlingService.checkStorageValidity();
                const prom2: Promise<any> = this._hubspotService.getDataByGuid(this.hubspotId, _hash);

                Promise.all([prom1, prom2]).then((proms) => {
                    if (proms[1].status && proms[1].status === 'error') {
                        this._$errorResponse.next(proms[1].message);
                    } else {
                        const _shouldPrepopData = proms[1].ignoreCurrentValues === false;
                        this._$currentHash.next(_hash);
                        this._hubspotService.setMapEntry(this.hubspotId, _hash);
                        this._hubspotService.setPrepopulationData(_shouldPrepopData, this._$currentHash.getValue()).then(() => {
                            this._setSubmitLabel(proms[1]);
                            this._$hubspotData.next(proms[1]);
                            const legalConsent = proms[1].metaData.filter((meta) => meta.name === 'legalConsentOptions');
                            if (legalConsent[0]) {
                                this._$legalConsents.next(JSON.parse(legalConsent[0].value));
                            }
                            this._setFormData(_shouldPrepopData);
                        });
                    }
                });
                if (this._sub) {
                    this._sub.unsubscribe();
                }
            }
        });
    }
}
